import React, { useEffect, useState } from 'react';
import { Globe, TrendingUp } from 'lucide-react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { unctadApi } from '../../../services/api';
import type { TradePartnerData } from '../../../services/api/unctadApi';

interface TradePartnersPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

// Country flag emojis
const COUNTRY_FLAGS: Record<string, string> = {
  CHN: '\ud83c\udde8\ud83c\uddf3',
  RUS: '\ud83c\uddf7\ud83c\uddfa',
  KAZ: '\ud83c\uddf0\ud83c\uddff',
  TUR: '\ud83c\uddf9\ud83c\uddf7',
  KOR: '\ud83c\uddf0\ud83c\uddf7',
  DEU: '\ud83c\udde9\ud83c\uddea',
  USA: '\ud83c\uddfa\ud83c\uddf8',
  AFG: '\ud83c\udde6\ud83c\uddeb',
  IND: '\ud83c\uddee\ud83c\uddf3',
  IRN: '\ud83c\uddee\ud83c\uddf7',
};

export const TradePartnersPanel: React.FC<TradePartnersPanelProps> = ({
  isMinimized,
  onMinimize,
}) => {
  const [data, setData] = useState<TradePartnerData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const response = await unctadApi.getTradePartners('UZB');
      if (response.data) {
        setData(response.data);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const maxTrade = data.length > 0 ? Math.max(...data.map(d => d.totalTrade)) : 0;

  const exportData = data.map(d => ({
    partner: d.partnerName,
    exports: d.exports,
    imports: d.imports,
    totalTrade: d.totalTrade,
    share: d.share,
    year: d.year,
    source: d.source,
  }));

  return (
    <Panel
      id="trade-partners"
      title="Trade Partners"
      subtitle="Top 5"
      status="static"
      isMinimized={isMinimized}
      onMinimize={onMinimize}
      data={exportData}
    >
      <div className="h-full flex flex-col p-3">
        {loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
            Loading trade partners...
          </div>
        ) : (
          <>
            {/* Header Stats */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
                <div className="text-2xl font-mono font-bold text-data-amber">
                  {data.reduce((sum, d) => sum + d.share, 0).toFixed(0)}%
                </div>
                <div className="text-xs text-gray-500">Top 5 Share</div>
              </div>
              <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
                <div className="text-2xl font-mono font-bold text-white">
                  ${(data.reduce((sum, d) => sum + d.totalTrade, 0) / 1000).toFixed(1)}B
                </div>
                <div className="text-xs text-gray-500">Combined Trade</div>
              </div>
            </div>

            {/* Partners List */}
            <div className="flex-1 space-y-3">
              {data.map((partner, idx) => (
                <div
                  key={partner.partnerCode}
                  className="bg-terminal-bg/40 rounded-lg p-3 border border-terminal-border/30"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{COUNTRY_FLAGS[partner.partnerCode] || '\ud83c\udff3\ufe0f'}</span>
                      <span className="text-sm font-semibold text-white">{partner.partnerName}</span>
                      {idx === 0 && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-data-amber/20 text-data-amber font-semibold">
                          #1
                        </span>
                      )}
                    </div>
                    <span className="text-lg font-mono font-bold text-data-amber">
                      {partner.share.toFixed(1)}%
                    </span>
                  </div>

                  {/* Trade Volume Bar */}
                  <div className="h-2.5 bg-terminal-border rounded-full overflow-hidden mb-2">
                    <div
                      className={`h-full rounded-full ${idx === 0 ? 'bg-data-amber' : 'bg-data-blue'}`}
                      style={{ width: `${(partner.totalTrade / maxTrade) * 100}%` }}
                    />
                  </div>

                  {/* Export/Import Split */}
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500">Export:</span>
                      <span className="font-mono text-data-green">${(partner.exports / 1000).toFixed(2)}B</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500">Import:</span>
                      <span className="font-mono text-data-red">${(partner.imports / 1000).toFixed(2)}B</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Footer */}
        <div className="mt-auto pt-2 border-t border-terminal-border/50 flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.UNCTAD} showLabel size="xs" />
          <span className="text-xs text-gray-500">2024</span>
        </div>
      </div>
    </Panel>
  );
};

export default TradePartnersPanel;
